import {useEffect} from 'react';
import {Conversation} from '@stripe-internal/conversations-client';
import {Subscription} from 'rxjs';
import {filter, take} from 'rxjs/operators';
import {defineMessages} from 'react-intl';
import {UHLiveChannelsClientType} from '../../Providers/ClientProvider/UHLiveChannelsClient';
import {useAssistantConversationEndedLogEvents} from './useAssistantConversationEndedLogEvents';

export const messages = defineMessages({
  chatEndedMessage: {
    id: 'support_conversations_client.supportWidget.chatEndedMessage',
    description:
      'Message to show to prompt user to close chat widget to start new chat',
    defaultMessage:
      'For further assistance, please close the window and start a new chat.',
  },
  newConversationPrompt: {
    id: 'support_conversations_client.supportWidget.newConversationPrompt',
    description:
      'Message to show to prompt user to enter a message to start a chat',
    defaultMessage: 'Send a message to let us know how we can help.',
  },
  waitingForAgent: {
    id: 'support_conversations_client.supportWidget.waitingForAgent',
    description:
      'Message to show to prompt user to enter a message to start a chat',
    defaultMessage: 'Waiting for an agent to join.',
  },
});

type Props = {
  conversation: Conversation;
};

export function useConversationLogEvents({conversation}: Props) {
  useAssistantConversationEndedLogEvents({conversation});

  useEffect(() => {
    if (conversation.client.type === UHLiveChannelsClientType) {
      // the UHLiveChannelsClient manages its own log event messages
      return;
    }

    const sub = new Subscription();

    sub.add(
      conversation
        .eventsObservable()
        .pipe(
          filter(
            (e) =>
              e.name === 'conversation-participant-joined' &&
              e.participant.role === 'agent',
          ),
        )
        .subscribe(() => {
          // when agents join, remove messages indicating we're connecting them
          const items = conversation.log.get();
          conversation.log.set(
            items.filter((item) => item.id !== 'connecting-agent'),
          );
        }),
    );

    if (
      conversation.userIsActive &&
      Object.keys(conversation.messages).length === 0
    ) {
      // if there are no messages yet, prompt the user to start a conversation
      conversation.log.appendMetaEvent({
        id: 'empty-conversation-prompt',
        message: messages.newConversationPrompt,
      });

      sub.add(
        conversation.log
          .observable()
          .pipe(
            filter((items) =>
              // Make sure that a message has been sent and that the message is from the user in the conversation
              items.some(
                (item) =>
                  item.name === 'conversation-message-added' &&
                  item.message.participant.id === conversation.user.id,
              ),
            ),
            take(1), // we only need the first time this happens
          )
          .subscribe(() => {
            // once a message is added, we'll remove the empty conversation prompt
            const items = conversation.log.get();
            conversation.log.set(
              items.filter((item) => item.id !== 'empty-conversation-prompt'),
            );

            if (
              conversation.userIsActive &&
              Object.keys(conversation.participants).length < 2
            ) {
              // if the agent hasn't joined yet, show a message that we're connecting them
              conversation.log.appendMetaEvent({
                id: 'connecting-agent',
                message: messages.waitingForAgent,
              });
            }
          }),
      );
    }

    sub.add(
      conversation
        .eventsObservable()
        .pipe(
          filter((e) => e.name === 'conversation-ended'),
          take(1),
        )
        .subscribe(() => {
          conversation.disconnect();
          conversation.log.appendMetaEvent({
            id: 'chat-ended-notice',
            message: messages.chatEndedMessage,
          });
        }),
    );

    return () => sub.unsubscribe();
  }, [conversation]);
}
